/**
 * Lobby "How to Play" panel — bottom half of the left column, pairs with the
 * top-10 leaderboard above it. Pure-DOM, zero-state component that renders a
 * numbered list of the viewer-facing game flow so new viewers know what to
 * type the moment they land on the stream.
 *
 * Adapted from WhatIs (`whatis/browser/src/ui/lobby-howto.ts`). Copy is
 * tailored for Anagram: rules are simpler — no votes/categories, just guess.
 */

export interface LobbyHowToHandle {
  el: HTMLDivElement;
  dispose: () => void;
}

const STYLES = `
  .an-howto {
    position: relative;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.75) 100%);
    border: 2px solid var(--sg-accent, #00BFA6);
    border-radius: 14px;
    box-shadow: 0 0 30px rgba(0,191,166,0.15), 0 4px 30px rgba(0,0,0,0.4);
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    pointer-events: auto;
  }
  .an-howto-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.22em;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 14px rgba(255,196,61,0.4);
    padding: 10px 8px 8px 8px;
    border-bottom: 1px solid rgba(0,191,166,0.35);
    flex: 0 0 auto;
  }
  .an-howto-body {
    flex: 0 0 auto;
    overflow: hidden;
    padding: 12px 16px 14px 16px;
  }
  .an-howto-list {
    list-style: none;
    margin: 0;
    padding: 0;
    counter-reset: an-howto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .an-howto-item {
    counter-increment: an-howto;
    position: relative;
    padding: 4px 0 4px 36px;
    font-size: 0.95rem;
    line-height: 1.45;
    color: #E2E8F0;
  }
  .an-howto-item::before {
    content: counter(an-howto);
    position: absolute;
    left: 0;
    top: 2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255,196,61,0.18);
    border: 1px solid var(--sg-primary, #FFC43D);
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    font-size: 0.9rem;
    text-align: center;
    line-height: 22px;
  }
  .an-howto-item code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.12);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
    font-size: 0.9em;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-howto-style')) return;
  const s = document.createElement('style');
  s.id = 'an-howto-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createLobbyHowTo(mount: HTMLElement): LobbyHowToHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'an-howto';
  el.setAttribute('data-role', 'lobby-howto');
  el.innerHTML = `
    <div class="an-howto-title">HOW TO PLAY</div>
    <div class="an-howto-body">
      <ol class="an-howto-list">
        <li class="an-howto-item">Streamer types <code>!start</code> to begin</li>
        <li class="an-howto-item">Jumbled letters appear with a category hint</li>
        <li class="an-howto-item">Type the unscrambled word in chat (no command prefix)</li>
        <li class="an-howto-item">First letter highlights at half-time as a hint</li>
        <li class="an-howto-item">Fastest correct answer wins the round &mdash; type <code>!score</code> to see yours</li>
      </ol>
    </div>
  `;
  mount.appendChild(el);
  return { el, dispose() { el.remove(); } };
}
